import React, { useState } from "react";
import { Tag, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

/**
 * Coupon code entry + applied state for cart/checkout.
 * Uses the public POST /api/coupons/validate endpoint.
 */
export default function CouponInput({ compact = false }) {
  const { coupon, applyCoupon, removeCoupon, subtotal } = useCart();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const c = code.trim();
    if (!c) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: c,
        subtotal,
      });
      applyCoupon({
        code: data.code,
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        description: data.description,
      });
      setCode("");
      if (data.discount_type === "free_shipping") {
        toast.success(`${data.code} applied — free shipping on this order!`);
      } else {
        toast.success(`${data.code} applied — you saved ${formatMoney(data.discount_amount)}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply coupon");
    } finally {
      setSubmitting(false);
    }
  };

  if (coupon) {
    return (
      <div
        className="flex items-center justify-between gap-3 bg-[var(--muted)]/50 border border-dashed border-[var(--terracotta)] rounded-xl px-4 py-3"
        data-testid="applied-coupon"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Tag className="w-4 h-4 text-[var(--terracotta)] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--ink-forest)] truncate" data-testid="applied-coupon-code">
              {coupon.code}
            </p>
            <p className="text-xs text-[var(--muted-fg)] truncate">
              {couponBlurb(coupon)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            removeCoupon();
            toast.message("Coupon removed.");
          }}
          className="shrink-0 p-1.5 rounded-full hover:bg-[var(--bg-cream)] text-[var(--muted-fg)] hover:text-[var(--terracotta)] transition-colors"
          aria-label="Remove coupon"
          data-testid="remove-coupon-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`flex items-stretch gap-2 ${compact ? "" : "mt-2"}`}
      data-testid="coupon-form"
    >
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Coupon code"
        className="flex-1 min-w-0 bg-[var(--bg-surface)] border border-[var(--border)] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[var(--terracotta)] uppercase tracking-wide"
        data-testid="coupon-input"
        disabled={submitting}
      />
      <button
        type="submit"
        disabled={submitting || !code.trim()}
        className="shrink-0 bg-[var(--ink-forest)] text-[var(--bg-cream)] rounded-full px-5 py-2 text-sm font-medium hover:bg-[var(--terracotta)] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
        data-testid="coupon-apply-btn"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
        Apply
      </button>
    </form>
  );
}

function couponBlurb(c) {
  if (c.discount_type === "percent") return `${c.discount_value}% off subtotal`;
  if (c.discount_type === "fixed") return `${formatMoney(c.discount_value)} off`;
  if (c.discount_type === "free_shipping") return "Free shipping";
  return c.description || "Applied";
}

function formatMoney(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}
